import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _assertThisInitialized from "@babel/runtime/helpers/esm/assertThisInitialized";
import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";
var _excluded = ["aria-label", "children", "enableSnap", "heading", "includeSkipLink", "onNextClick", "onPreviousClick", "onScroll", "renderNavigationControls", "role", "scrollOffset", "slideAlign", "slideGap", "slideWidth", "hasFocusableContent"];
import PropTypes from 'prop-types';
import React, { Children, Component, createRef } from 'react';
import { KEY_CODES } from '../../constants';
import CustomPropTypes from '../../util/custom-prop-types';
import { CarouselHeader } from '../CarouselHeader/CarouselHeader';
import { CarouselHeadingArea } from '../CarouselHeadingArea/CarouselHeadingArea';
import { CarouselNavControls } from '../CarouselNavControls/CarouselNavControls';
import { CarouselScrollContainer } from '../CarouselScrollContainer/CarouselScrollContainer';
import { CarouselSkipLink } from '../CarouselSkipLink/CarouselSkipLink';
import { CarouselSlide } from '../CarouselSlide/CarouselSlide';
import { StyledCarousel } from './Carousel.styles';

/**
 * @since 8.71.0
 */

export var Carousel = /*#__PURE__*/function (_Component) {
  _inheritsLoose(Carousel, _Component);
  function Carousel(props) {
    var _this;
    _this = _Component.call(this, props) || this;
    _this.updateNavControlStates = function () {
      if (_this.scrollWrapper.current) {
        _this.enablePreviousAction(_this.scrollWrapper.current.scrollLeft === 0);
        // Add 1px to account for potential rounding issues
        _this.enableNextAction(_this.scrollWrapper.current.scrollWidth <= _this.scrollWrapper.current.clientWidth + _this.scrollWrapper.current.scrollLeft + 1);
      }
    };
    _this.handleOnScroll = function (e, onScroll) {
      _this.updateNavControlStates();
      if (typeof onScroll === 'function') {
        onScroll(e);
      }
    };
    _this.getSlideWidth = function () {
      var slideWidth = _this.props.slideWidth;
      if (slideWidth > 0) {
        return slideWidth;
      }

      // If no `slideWidth` value, try using the width of the first slide.
      var firstSlideEl = _this.firstSlide.current;
      if (firstSlideEl) {
        return firstSlideEl.getBoundingClientRect().width;
      }
      return 0;
    };
    _this.nextPage = function () {
      /**
       * Reducing the scroll amount by half the width of a slide fixes
       * the problem when a slide is mostly visible on one page but
       * then gets completely skipped when you navigate to the next page.
       */
      var onNextClick = _this.props.onNextClick;
      var scrollEl = _this.scrollWrapper.current;
      var rect = scrollEl.getBoundingClientRect();
      var halfSlideWidth = _this.getSlideWidth() * 0.5;
      _this.scrollBy(rect.width - halfSlideWidth);
      onNextClick();
    };
    _this.previousPage = function () {
      var onPreviousClick = _this.props.onPreviousClick;
      var scrollEl = _this.scrollWrapper.current;
      var rect = scrollEl.getBoundingClientRect();
      var halfSlideWidth = _this.getSlideWidth() * 0.5;
      _this.scrollBy(-rect.width + halfSlideWidth);
      onPreviousClick();
    };
    _this.scrollToFocusedElement = function (evt) {
      /**
       * When tabbing, this tries to ensure the focused element is always visible.
       * If focused element is outside of the Carousel container, scroll by at least
       * one slide width.
       */
      if (evt.keyCode === KEY_CODES.TAB) {
        var focusElement = document.activeElement;
        var scrollEl = _this.scrollWrapper.current;
        var focusPosition = focusElement.getBoundingClientRect();
        var parentPosition = scrollEl.getBoundingClientRect();
        if (focusPosition && parentPosition) {
          if (focusPosition.right > parentPosition.right) {
            var overscrollAmount = focusPosition.right - parentPosition.right;
            var slideWidth = _this.getSlideWidth();
            var scrollAmount = overscrollAmount <= slideWidth ? slideWidth : overscrollAmount + slideWidth;
            _this.scrollBy(scrollAmount);
          }
          if (focusPosition.left < parentPosition.left) {
            var _overscrollAmount = parentPosition.left - focusPosition.left;
            var _slideWidth = _this.getSlideWidth();
            var _scrollAmount = _overscrollAmount <= _slideWidth ? _slideWidth : _overscrollAmount + _slideWidth;
            _this.scrollBy(-_scrollAmount);
          }
        }
      }
    };
    _this.firstSlide = /*#__PURE__*/createRef();
    _this.scrollWrapper = /*#__PURE__*/createRef();
    _this.sectionStart = /*#__PURE__*/createRef();
    _this.sectionEnd = /*#__PURE__*/createRef();
    _this.state = {
      previousEnabled: false,
      nextEnabled: false
    };
    _this.handleOnScroll = _this.handleOnScroll.bind(_assertThisInitialized(_this));
    return _this;
  }
  var _proto = Carousel.prototype;
  _proto.componentDidMount = function componentDidMount() {
    var scrollLeft = this.props.scrollLeft;
    if (this.scrollWrapper.current) {
      if (scrollLeft > 0) {
        this.scrollWrapper.current.scrollLeft = scrollLeft;
      }
    }
    window.addEventListener('resize', this.updateNavControlStates);
    this.updateNavControlStates();
  };
  _proto.shouldComponentUpdate = function shouldComponentUpdate(nextProps) {
    var scrollLeft = this.props.scrollLeft;
    var scrollEl = this.scrollWrapper.current;
    if (nextProps.scrollLeft !== scrollLeft) {
      var scrollDiff = nextProps.scrollLeft - scrollEl.getBoundingClientRect().left;
      this.scrollBy(scrollDiff);
    }
    return true;
  };
  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    if (prevProps !== this.props) {
      // We don't need to call this when we have a state change, only when we're
      // given new props
      this.updateNavControlStates();
    }
  };
  _proto.componentWillUnmount = function componentWillUnmount() {
    window.removeEventListener('resize', this.updateNavControlStates);
  };
  _proto.scrollBy = function scrollBy(pixels) {
    var scrollEl = this.scrollWrapper.current;
    scrollEl.scrollLeft += pixels;
  };
  _proto.enableNextAction = function enableNextAction(atEnd) {
    var nextEnabled = this.state.nextEnabled;
    if (atEnd) {
      if (nextEnabled) {
        this.setState({
          nextEnabled: false
        });
      }
    } else if (!nextEnabled) {
      this.setState({
        nextEnabled: true
      });
    }
  };
  _proto.enablePreviousAction = function enablePreviousAction(atStart) {
    var previousEnabled = this.state.previousEnabled;
    if (atStart) {
      if (previousEnabled) {
        this.setState({
          previousEnabled: false
        });
      }
    } else if (!previousEnabled) {
      this.setState({
        previousEnabled: true
      });
    }
  };
  _proto.render = function render() {
    var _this2 = this;
    var _this$props = this.props,
      ariaLabel = _this$props['aria-label'],
      children = _this$props.children,
      enableSnap = _this$props.enableSnap,
      heading = _this$props.heading,
      includeSkipLink = _this$props.includeSkipLink,
      onNextClick = _this$props.onNextClick,
      onPreviousClick = _this$props.onPreviousClick,
      _onScroll = _this$props.onScroll,
      renderNavigationControls = _this$props.renderNavigationControls,
      role = _this$props.role,
      scrollOffset = _this$props.scrollOffset,
      slideAlign = _this$props.slideAlign,
      slideGap = _this$props.slideGap,
      slideWidth = _this$props.slideWidth,
      hasFocusableContent = _this$props.hasFocusableContent,
      rest = _objectWithoutProperties(_this$props, _excluded);
    var cells = Children.map(children, function (child, index) {
      return /*#__PURE__*/React.createElement(CarouselSlide, {
        ref: index === 0 ? _this2.firstSlide : undefined,
        slideAlign: slideAlign,
        slideWidth: slideWidth
      }, child);
    });
    var _this$state = this.state,
      previousEnabled = _this$state.previousEnabled,
      nextEnabled = _this$state.nextEnabled;
    return /*#__PURE__*/React.createElement(StyledCarousel, _extends({
      role: role,
      "aria-label": ariaLabel
    }, rest), /*#__PURE__*/React.createElement(CarouselHeader, null, /*#__PURE__*/React.createElement(CarouselHeadingArea, null, heading && heading), renderNavigationControls && renderNavigationControls({
      onClick: this.previousPage,
      disabled: !previousEnabled
    }, {
      onClick: this.nextPage,
      disabled: !nextEnabled
    })), includeSkipLink ? /*#__PURE__*/React.createElement(CarouselSkipLink, {
      onClick: function onClick(e) {
        _this2.sectionEnd.current.focus();
        e.preventDefault();
      },
      ref: this.sectionStart
    }, "Skip to the end of the carousel") : null, /*#__PURE__*/React.createElement(CarouselScrollContainer, {
      enableSnap: enableSnap,
      hasFocusableContent: hasFocusableContent,
      onKeyUp: this.scrollToFocusedElement,
      onScroll: function onScroll(e) {
        return _this2.handleOnScroll(e, _onScroll);
      },
      scrollOffset: scrollOffset,
      slideGap: slideGap,
      ref: this.scrollWrapper
    }, cells), includeSkipLink ? /*#__PURE__*/React.createElement(CarouselSkipLink, {
      onClick: function onClick(e) {
        _this2.sectionStart.current.focus();
        e.preventDefault();
      },
      ref: this.sectionEnd
    }, "Skip to the beginning of the carousel") : null);
  };
  return Carousel;
}(Component);
Carousel.displayName = 'Carousel';
Carousel.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * When true, the carousel will "snap" to the nearest slide after the user
   * clicks a nav control or finishes scrolling or swiping.
   *
   * By default, snap is enabled on desktop but disabled on mobile to protect
   * touch users from clunky, unexpected behavior. If needed, this can be changed
   * using an inline media query object.
   *
   * See the `slideAlign` and `scrollOffset` props for more controls.
   */
  enableSnap: CustomPropTypes.inlineMediaQuery(PropTypes.bool),
  /**
   * When false, the Carousel's content area will be focusable so that
   * users can control the container's scroll position with their keyboard
   */
  hasFocusableContent: PropTypes.bool,
  /**
   * A heading to display above the Carousel. Accepts a node (ex: `Heading`)
   * or a fragment with multiple nodes (ex: `Heading` + `Paragraph`).
   *
   * Try to provide heading copy that's descriptive enough so that a screen reader user
   * can decide whether they would want to view it or skip over it.
   */
  heading: PropTypes.node,
  /**
   * Callback function that's called when you click on the nav button that shows
   * the next set of slides.
   */
  onNextClick: PropTypes.func,
  /**
   * Callback function that's called when you click on the nav button that shows
   * the previous set of slides.
   */
  onPreviousClick: PropTypes.func,
  /**
   * An event handler that will be called with the carousel has been scrolled either via mouse,
   * swipe or the navigation buttons.
   */
  onScroll: PropTypes.func,
  /**
   * An array of slide content. Each piece of content will be wrapped in a `Carousel.Slide`
   * parent.
   */
  children: PropTypes.node,
  /**
   * Use to scroll the carousel to a specified position. Takes a number representing the number
   * of pixels to scroll from the beginning of the carousel.
   *
   * This prop may not work as expected when `enableSnap` is true.
   *
   * This feature comes via Trulia's ContentSlider. However it is not one of the requirements
   * so ignoring for now. We might replace it with the ability to autoscroll to a specific slide
   * index. However, any type of autoscrolling could cause confusion for keyboard users since
   * tabbing into the carousel starts them at the very first slide.
   *
   * Currently, we're primarily using this prop for unit testing.
   *
   * @ignore
   */
  scrollLeft: PropTypes.number,
  /**
   * When true, keyboard users will see a "skip" link they can use to skip past all of
   * the carousel content.
   *
   * "Skip" links appear at the beginning and end of a carousel.
   *
   * Note that MacOS/Firefox users can only tab to links if they enable it in their
   * System Preferences.
   */
  includeSkipLink: PropTypes.bool,
  /**
   * A render prop that returns the navigation controls. It takes two arguments: props
   * objects for the left and right buttons.
   */
  renderNavigationControls: PropTypes.func,
  /**
   * Sets padding for the left and right sides of the slide container. Useful for preventing
   * box shadows from getting cropped.
   *
   * Takes a spacing t-shirt size or number multiplier. Defaults to '0'.
   *
   * Supports inline media query objects.
   */
  scrollOffset: CustomPropTypes.inlineMediaQuery(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  /**
   * When `enableSnap` is true, `slideAlign` determines how a slide should try to
   * align within its container. Applied using
   * [`scroll-snap-align`](https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-snap-align).
   */
  slideAlign: CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(['start', 'end', 'center'])),
  /**
   * Sets the gutter size between slides.
   *
   * Takes a spacing t-shirt size or number multiplier. Defaults to 'xs'.
   *
   * Supports inline media query objects.
   */
  slideGap: CustomPropTypes.inlineMediaQuery(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  /**
   * Sets the width of a slide using `flex-basis`. Takes a number or the,
   * default, `auto`.
   *
   * Supports inline media query objects.
   */
  slideWidth: CustomPropTypes.inlineMediaQuery(PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]))
} : {};
Carousel.defaultProps = {
  children: null,
  enableSnap: {
    default: false,
    lg_gte: true
  },
  hasFocusableContent: true,
  onNextClick: function onNextClick() {},
  onPreviousClick: function onPreviousClick() {},
  scrollLeft: 0,
  scrollOffset: 0,
  slideAlign: 'start',
  slideGap: 'sm',
  slideWidth: 'auto',
  includeSkipLink: true,
  renderNavigationControls: function renderNavigationControls(leftButtonProps, rightButtonProps) {
    return /*#__PURE__*/React.createElement(CarouselNavControls, {
      leftButtonProps: leftButtonProps,
      rightButtonProps: rightButtonProps
    });
  }
};
Carousel.Header = CarouselHeader;
Carousel.HeadingArea = CarouselHeadingArea;
Carousel.NavControls = CarouselNavControls;
Carousel.ScrollContainer = CarouselScrollContainer;
Carousel.SkipLink = CarouselSkipLink;
Carousel.Slide = CarouselSlide;